import React from 'react';
import { useToasts } from '../ui/ToastProvider';

/** A named library view stored in the viewer's ui_prefs (follows them to any device). */
export type SavedLibraryView<C> = { id: string; name: string; config: C };

/** The slice of `useUserPrefs()` this hook needs. Pass the page's own instance; never create a second one. */
export type SavedViewsPrefs = {
  /** Changes identity whenever the stored prefs change (including when they first load). */
  get: <T>(path: string, fallback: T) => T;
  set: (path: string, value: unknown) => void;
  flush: () => void;
};

export type UseSavedViewsOptions<C> = {
  prefs: SavedViewsPrefs;
  /** ui_prefs path of the view list, e.g. `events.savedViews`. */
  viewsKey: string;
  /** ui_prefs path of the default view id, e.g. `events.defaultViewId`. */
  defaultKey: string;
  /** The page's current filter state, as a view config. */
  current: C;
  /** Applies a stored config to the page's filters. */
  apply: (config: C) => void;
  /** Compares two configs after the page's own normalisation. */
  equals: (left: C | null | undefined, right: C | null | undefined) => boolean;
};

export type SavedViewsController<C> = {
  views: SavedLibraryView<C>[];
  defaultViewId: string | null;
  appliedId: string | null;
  setAppliedId: (id: string | null) => void;
  appliedView: SavedLibraryView<C> | null;
  hasChanges: boolean;
  canUpdate: boolean;
  applyView: (id: string) => void;
  saveView: () => void;
  updateView: () => void;
  removeView: (id: string) => void;
  setDefaultView: (id: string | null) => void;
};

/**
 * Saved views for a library page: list, apply, save, update, remove and the default view.
 * Extracted unchanged from the Events library (prompt text, toasts and flush points included);
 * the page keeps its own config shape, normalisation and default-view loading.
 */
export function useSavedViews<C>({
  prefs,
  viewsKey,
  defaultKey,
  current,
  apply,
  equals,
}: UseSavedViewsOptions<C>): SavedViewsController<C> {
  const { get, set, flush } = prefs;
  const { push } = useToasts();
  const [appliedId, setAppliedId] = React.useState<string | null>(null);

  const views = React.useMemo(() => {
    const raw = get<SavedLibraryView<C>[]>(viewsKey, []);
    return Array.isArray(raw) ? raw : [];
  }, [get, viewsKey]);
  const defaultViewId = get<string | null>(defaultKey, null);

  const appliedView = React.useMemo(() => {
    if (!appliedId) return null;
    return views.find((view) => view.id === appliedId) ?? null;
  }, [appliedId, views]);
  const hasChanges = React.useMemo(() => {
    if (!appliedView) return false;
    return !equals(appliedView.config, current);
  }, [appliedView, current, equals]);
  const canUpdate = Boolean(appliedView && hasChanges);

  const applyView = React.useCallback((id: string) => {
    if (!id) {
      setAppliedId(null);
      return;
    }
    const view = views.find((candidate) => candidate.id === id);
    if (!view?.config) return;
    apply(view.config);
    setAppliedId(id);
  }, [apply, views]);

  const saveView = React.useCallback(() => {
    const name = window.prompt('Name this view');
    if (name == null || !name.trim()) return;
    const id = crypto.randomUUID();
    const next = [...views, { id, name: name.trim(), config: current }];
    set(viewsKey, next);
    setAppliedId(id);
    flush();
    push({ type: 'success', message: 'View saved' });
  }, [current, flush, push, set, views, viewsKey]);

  const updateView = React.useCallback(() => {
    if (!appliedView || !hasChanges) return;
    const next = views.map((view) => (
      view.id === appliedView.id ? { ...view, config: current } : view
    ));
    set(viewsKey, next);
    flush();
    push({ type: 'success', message: `${appliedView.name} updated` });
  }, [appliedView, current, flush, hasChanges, push, set, views, viewsKey]);

  const removeView = React.useCallback((id: string) => {
    if (!id) return;
    const next = views.filter((view) => view.id !== id);
    set(viewsKey, next);
    if (defaultViewId === id) set(defaultKey, null);
    if (appliedId === id) setAppliedId(null);
    flush();
  }, [appliedId, defaultKey, defaultViewId, flush, set, views, viewsKey]);

  const setDefaultView = React.useCallback((id: string | null) => {
    set(defaultKey, id || null);
    flush();
  }, [defaultKey, flush, set]);

  React.useEffect(() => {
    if (!appliedId) return;
    if (views.some((view) => view.id === appliedId)) return;
    setAppliedId(null);
  }, [appliedId, views]);

  return {
    views,
    defaultViewId,
    appliedId,
    setAppliedId,
    appliedView,
    hasChanges,
    canUpdate,
    applyView,
    saveView,
    updateView,
    removeView,
    setDefaultView,
  };
}

export default useSavedViews;
